// ============================================================================
// FILE: src/modules/invoice/pages/InvoiceDetailPage.jsx
// CREATED: 2025-12-03
// UPDATED: 2025-12-04
// PURPOSE: Display invoice details: extracted fields + line items.
// COMMENTS:
// - Added Back button using useNavigate()
// - No tenant/location arguments; backend uses JWT
// ============================================================================

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import invoiceService from "../services/invoiceService";
import InvoiceLineItemTable from "../components/InvoiceLineItemTable";
import ExtractedFieldViewer from "../components/ExtractedFieldViewer";

const InvoiceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadInvoice();
  }, [id]);

  const loadInvoice = async () => {
    try {
      setLoading(true);
      setError(null);

      // 🔥 no tenant/location here — backend extracts from JWT
      const response = await invoiceService.getInvoiceDetail(id);

      if (response.success) {
        setInvoice(response.data);
      } else {
        setError(response.message || "Unable to load invoice details.");
      }
    } catch (err) {
      console.error("Invoice load error:", err);
      setError("An error occurred while loading invoice.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="p-6">Loading invoice...</div>;
  if (error) return <div className="p-6 text-red-600">{error}</div>;
  if (!invoice) return <div className="p-6">Invoice not found.</div>;

  const items = invoice.items || [];

  return (
    <div className="p-6">

      {/* ===========================
          BACK BUTTON
      ============================ */}
      <div className="mb-4">
        <button
          onClick={() => navigate(-1)}
          className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md"
        >
          ← Back
        </button>
      </div>

      {/* PAGE TITLE */}
      <h1 className="text-2xl font-bold mb-4">Invoice Details</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

        {/* Extracted Fields */}
        <div className="bg-white shadow rounded-lg p-4 md:col-span-2">
          <h2 className="text-xl font-semibold mb-3">Extracted Header Fields</h2>
          <ExtractedFieldViewer data={invoice} />
        </div>

      </div>

      {/* Line items section */}
      <div className="mt-6 bg-white shadow rounded-lg p-4">
        <h2 className="text-xl font-semibold mb-3">Line Items</h2>
        <InvoiceLineItemTable items={items} />
      </div>

    </div>
  );
};

export default InvoiceDetailPage;
